import { StyleState } from '@/components/map/utils/layer-style-helpers';
import { APP_SETTINGS } from '@/lib/config';
import type { StyleV2 } from '@/utils/layer-sources/style-v2';

export const STYLE_MODE = {
  FIXED: 'fixed',
  CATEGORY: 'category',
  RANGE: 'range',
  ZOOM_RANGE: 'zoom-range',
} as const;

export const STYLE_PROPERTY_DIRECTION = {
  ASCENDING: 'ascending',
  DESCENDING: 'descending',
} as const;

export type StylePropertyDirection =
  (typeof STYLE_PROPERTY_DIRECTION)[keyof typeof STYLE_PROPERTY_DIRECTION];

export const DEFAULT_SCHEMES = {
  CATEGORY: 'Accent',
  RANGE: 'green-orange',
} as const;

export const LABEL_MODE = {
  NONE: 'none',
  SINGLE_COLUMN: 'single_column',
  TEMPLATE: 'template',
} as const;

export type LabelModeValue = (typeof LABEL_MODE)[keyof typeof LABEL_MODE];

export const LABEL_FONT_STYLE_VALUES = [
  'regular',
  'medium',
  'bold',
  'regular_italic',
  'medium_italic',
  'bold_italic',
] as const;

export type LabelFontStyle = (typeof LABEL_FONT_STYLE_VALUES)[number];

export const LABEL_FONT_STYLES = {
  REGULAR: 'regular',
  MEDIUM: 'medium',
  BOLD: 'bold',
  REGULAR_ITALIC: 'regular_italic',
  MEDIUM_ITALIC: 'medium_italic',
  BOLD_ITALIC: 'bold_italic',
} as const satisfies Record<string, LabelFontStyle>;

// UI requires percentages 0-100 for sliders/inputs while maplibre expects 0-1, that's why we need to convert opacity values
export const DEFAULT_STYLE_VALUES: StyleState = {
  // Point/Circle styles
  iconId: null,
  size: 6,
  sizeLogic: {
    sizeBy: null,
    minSize: 5,
    maxSize: 15,
    direction: STYLE_PROPERTY_DIRECTION.ASCENDING,
  },
  fillColor: '#2DD4BF',
  fillOpacity: 100,
  strokeColor: '#000000',
  strokeWidth: 1,
  strokeOpacity: 100,
  borderType: 'solid',
  opacity: 100,
  // Polygon fill styles
  polygonFillColor: '#2DD4BF',
  polygonFillOpacity: 80,
  // Polygon stroke styles
  polygonStrokeColor: '#000000',
  polygonStrokeWidth: 1,
  polygonStrokeOpacity: 100,
  // Line styles
  lineColor: '#2DD4BF',
  lineWidth: 3,
  lineOpacity: 80,
  colorLogicColumn: '',
  colorLogicScheme: '',
  colorLogicType: 'category',
  colorLogicEnabled: false,
  labelLogic: {
    labelBy: null,
    size: 12,
    color: '#000000',
    haloColor: '#ffffff',
    haloWidth: 2,
    fontStyle: LABEL_FONT_STYLES.REGULAR,
    textVariableAnchor: 'center',
    textRadialOffset: 0,
  },
};

export const DEFAULT_STYLE_V2 = {
  version: APP_SETTINGS.CURRENT_STYLE_VERSION,
  polygon: {
    fill: {
      color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.polygonFillColor },
      opacity: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.polygonFillOpacity / 100 },
    },
    border: {
      color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.polygonStrokeColor },
      opacity: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.polygonStrokeOpacity / 100 },
      width: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.polygonStrokeWidth },
      borderType: DEFAULT_STYLE_VALUES.borderType,
    },
    label: {
      columnId: null,
      size: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.size },
      color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.color },
      haloColor: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.haloColor },
      haloWidth: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.haloWidth },
      fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
    },
  },

  line: {
    color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.lineColor },
    opacity: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.lineOpacity / 100 },
    width: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.lineWidth },
    borderType: DEFAULT_STYLE_VALUES.borderType,
  },

  point: {
    icon: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.iconId },
    size: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.size },
    color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.fillColor },
    opacity: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.fillOpacity / 100 },
    strokeColor: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.strokeColor },
    strokeOpacity: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.strokeOpacity / 100 },
    strokeWidth: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.strokeWidth },
    label: {
      columnId: null,
      size: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.size },
      color: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.color },
      haloColor: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.haloColor },
      haloWidth: { mode: STYLE_MODE.FIXED, value: DEFAULT_STYLE_VALUES.labelLogic.haloWidth },
      fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
      textVariableAnchor: DEFAULT_STYLE_VALUES.labelLogic.textVariableAnchor,
      textRadialOffset: DEFAULT_STYLE_VALUES.labelLogic.textRadialOffset,
    },
  },
  // Raster opacity is excluded: these are the geometry-section defaults seeded
  // for vector layers; raster layers carry their own opacity-only style.
} satisfies Required<Omit<StyleV2, 'raster'>>;
